import * as React from 'react';
import * as _ from 'lodash';
import moment from 'moment';

import * as Edge from '../../core';
import DownloadButton from '../downloadButton';
import Select from '../global/select';
import Input from '../global/input';
import PercentileGauge from './percentileGauge';
import { colorRanges } from '../../pages/training/helper';
import { SessionService } from '../../services/sessionService';
import { buildKeyTakeaway, getOrdinalSuffix, getSkillScoreCards } from './utils';

import { ReactComponent as CalendarIcon } from '../../assets/images/icons/Calendar.svg';
import { ReactComponent as DownloadIcon } from '../../assets/images/icons/Download.svg';
import { ReactComponent as StarIcon } from '../../assets/images/icons/Star.svg';

import './index.scss';

export interface PerformanceSummaryProps {
	embedded?: boolean;
	isFirstEvaluation?: boolean;
	sectionNumber?: number | string;
	session?: Edge.Models.BaseSessionResponse;
	sessions?: Edge.Models.BaseSessionResponse[];
	showProCompare?: boolean;
}

const PerformanceSummary: React.FunctionComponent<PerformanceSummaryProps> = ({
	embedded,
	isFirstEvaluation: isFirstEvaluationProp,
	sectionNumber = 1,
	session: sessionProp,
	sessions,
	showProCompare,
}) => {
	const [currentIndex, setCurrentIndex] = React.useState(0);
	const [isProCompare, setIsProCompare] = React.useState(false);

	const orderedSessions = sessions && sessions.length > 0 ? _.orderBy(sessions, 'expiresUtc', 'desc') : undefined;

	const session = orderedSessions ? orderedSessions[currentIndex] : sessionProp!;
	const oldestSession = orderedSessions ? _.orderBy(orderedSessions, 'expiresUtc', 'asc')[0] : undefined;
	const isFirstEvaluation =
		isFirstEvaluationProp !== undefined
			? isFirstEvaluationProp
			: !!(oldestSession && session.sessionId === oldestSession.sessionId);

	const edgeScore = session.edgeScore !== undefined && session.edgeScore !== null ? session.edgeScore * 100 : 0;
	const edgePercentile = session.explanationPercentile || 0;
	const skillCards = getSkillScoreCards(session);
	const keyTakeaway = buildKeyTakeaway(skillCards);
	const testDate = session.expiresUtc ? moment(session.expiresUtc).format(Edge.Constants.DATE_FORMAT) : '';

	const changeEvaluation = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setCurrentIndex(+event.target.value);
	};

	const onProCompareChecked = () => {
		setIsProCompare((prev) => !prev);
	};

	return (
		<section
			className={`performance-summary evaluation-results_section${
				embedded ? ' performance-summary_embedded' : ' evaluation_panel'
			}`}
		>
			<header className="evaluation-results_section_header">
				{!embedded && (
					<div className="evaluation_panel_heading">
						<span className="evaluation_skill_number">{sectionNumber}</span>
						<h2 className="evaluation_section_label">Your Performance Summary</h2>
					</div>
				)}
				<div className="evaluation-results_header_controls">
					{embedded && orderedSessions && (
						<Select value={currentIndex} onChange={changeEvaluation}>
							{orderedSessions.map((s, ix) => (
								<option key={s.sessionId} value={ix}>
									{moment(s.expiresUtc).format(Edge.Constants.DATE_FORMAT)}
								</option>
							))}
						</Select>
					)}
					<div className="evaluation-results_download-wrap">
						{embedded && showProCompare && (
							<div className="evaluation-results_pro-compare no_print">
								<Input
									type="checkbox"
									name="proCompare"
									id="proCompare"
									checked={isProCompare}
									onChange={onProCompareChecked}
								/>
								<label className="checkbox_label" htmlFor="proCompare">
									Compare with Pros
								</label>
							</div>
						)}
						<DownloadButton
							className="evaluation-results_download"
							defaultFileName={`evaluation_${session.sessionId}_${moment(new Date()).format(
								Edge.Constants.FILE_DATE_FORMAT
							)}${isProCompare ? '_pro' : ''}.pdf`}
							onClick={() => SessionService.getPrintableSession(session.sessionId, isProCompare)}
						>
							<DownloadIcon className="evaluation-results_download_icon" aria-hidden="true" />
							Download Full Edge Test Report (PDF)
						</DownloadButton>
					</div>
				</div>
			</header>

			<div className="evaluation-results_summary">
				<div className="evaluation-results_card evaluation-results_summary_card">
					<div className="evaluation-results_edge-score-row">
						<span className="evaluation-results_edge-score_label">EDGE SCORE</span>
						<div className="evaluation-results_edge-score">
							<span className="evaluation-results_edge-score_value">{edgeScore.toFixed(1)}</span>
							<span className="evaluation-results_edge-score_max">/ 100</span>
						</div>
					</div>
					<p className="evaluation_snapshot_text evaluation-results_summary-copy">
						{isFirstEvaluation
							? 'This is your starting point. Every session you complete helps build stronger visual skills.'
							: 'Keep building on your progress—consistent training is what moves the needle.'}
					</p>
					<div className="evaluation-results_test-date">
						<CalendarIcon />
						<span>Test Date: {testDate}</span>
					</div>
				</div>

				<div className="evaluation-results_card evaluation-results_summary_card evaluation-results_summary_card_gauge">
					<PercentileGauge percentile={edgePercentile} rankingText={session.explanationRankingText} />
				</div>

				<div className="evaluation-results_card evaluation-results_summary_card">
					<div className="evaluation_panel_heading">
						<StarIcon />
						<h3 className="evaluation_section_label">Key Takeaway</h3>
					</div>
					<p className="evaluation_snapshot_text">{keyTakeaway.summary}</p>
					<p className="evaluation-results_takeaway_emphasis">{keyTakeaway.encouragement}</p>
				</div>
			</div>

			<div className="evaluation-results_skills">
				<h2 className="evaluation_section_label">Your Visual Skill Rankings</h2>
				<div className="evaluation-results_skills_legend">
					{colorRanges.map((range) => (
						<span key={range.label}>
							<i style={{ backgroundColor: range.color }} />
							{range.label} {range.min === 0 && '(<25th)'}
							{range.min === 90 && '(90th+)'}
							{range.min !== 0 && range.min !== 90 && `(${range.min}th - ${range.max}th)`}
						</span>
					))}
				</div>
				<div className="evaluation-results_skills_grid">
					{skillCards.map((card) => (
						<div className="evaluation-results_card evaluation-results_skill-card" key={card.title}>
							<div className="evaluation-results_skill-card_icon">
								<card.Icon />
							</div>
							<div className="evaluation-results_skill-card_name">{card.title}</div>
							<div className="evaluation-results_skill-card_score">
								{card.score}
								{getOrdinalSuffix(card.score)}
							</div>
							<div className="evaluation-results_skill-card_percentile">Percentile</div>
							<div className="evaluation-results_skill-card_status" style={{ color: card.color }}>
								{card.statusLabel}
							</div>
							<div
								className="evaluation-results_skill-card_bar"
								style={{ backgroundColor: card.color }}
							/>
						</div>
					))}
				</div>
			</div>
		</section>
	);
};

export default PerformanceSummary;
